import { Button } from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import { useNavigate, useParams } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { addSingleFile as apiAddSingleFile } from '~/api/files';
import { submitDataRequestsV2 as apiSubmitRequest } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { type DataRequestV2, RequestType } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { TableV2 } from '~/components2/Table';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import store from '../../../../store';
import { DrawerContainer, DrawerContent, DrawerFooter } from '../../../Drawer';
import { PreviousButton } from '../../StepButtons';
import { pendingMapsRequestIds } from '../SelectAssets/columns';
import { columns } from './columns';

export default function Summary() {
  const [isProcessing, setIsProcessing] = useState(false);
  const { constraints, formState, agencies, mapRequestAgencyIds, asbuiltRequestAgencyIds, project } = useSnapshot(
    store,
  ) as typeof store;
  const { projectId, tabId } = useParams({ strict: false }) as ProjectDetailsParams;
  const navigate = useNavigate();

  const getIsDisabled = (): boolean => {
    // if narrative not confirmed
    if (!formState.narrativeConfirmed) return true;

    // if no maps AND no as-builts selected,
    if (!formState.mapsSelection.size && !formState.asbuiltsSelection.size) {
      return true;
    }

    // if constraints exist and files are NOT confirmed
    if (!constraints.acknowledged) {
      return true;
    }

    // else, not disabled
    return false;
  };

  const isDisabled = getIsDisabled();

  const data = useMemo(() => {
    return Object.values(agencies)
      .filter((a) => formState.mapsSelection.has(a.id) || formState.asbuiltsSelection.has(a.id))
      .sort((a, b) => a.agencyName.localeCompare(b.agencyName));
  }, [formState, agencies]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const files = useMemo(() => {
    if (!constraints.hasFileConstraints) return null;

    const items = Object.keys(formState.files).map((id) => {
      const f = formState.files[id];
      const name = f.get('name')?.toString() || 'UNNAMED FILE';

      return (
        <div key={`file-summary-${id}`} className="flex w-full items-center justify-start border-b">
          {name}
        </div>
      );
    });

    return (
      <div className="space-y-2">
        <Label label="Uploaded Files" step={2} />
        <div className="flex w-full flex-col">{isEmpty(items) ? 'No files selected' : items}</div>
      </div>
    );
  }, [formState]);

  const submitRequest = async () => {
    if (isDisabled) return;

    if (project?.sandboxProject === true) {
      Notify.info({
        message: 'Your data requests have been submitted. Click NEXT to move to the next step in the product tour.',
      });
      return;
    }

    setIsProcessing(true);

    const allAgencyIds = Object.keys(agencies);

    const requests: Partial<DataRequestV2>[] = [];

    const getId = (id: string) => {
      return id as unknown as DataRequestV2['agencyId'];
    };

    for (const id of pendingMapsRequestIds(formState.mapsSelection, agencies, mapRequestAgencyIds)) {
      requests.push({
        agencyId: getId(id),
        type: RequestType.MapsRequested,
      });
    }

    formState.asbuiltsSelection.forEach((id) => {
      if (!asbuiltRequestAgencyIds.has(id) && allAgencyIds.includes(id)) {
        requests.push({
          agencyId: getId(id),
          type: RequestType.AsbuiltsRequested,
        });
      }
    });

    if (isEmpty(requests)) {
      Notify.error({
        title: 'Submit Data Requests',
        message: 'Invalid request count',
      });

      return;
    }

    try {
      // add files to project first;
      if (!isEmpty(formState.files)) {
        const uploads = Object.values(formState.files).map(async (f) => {
          return await apiAddSingleFile(projectId, f);
        });

        await Promise.all(uploads);
      }

      // after file uploads, submit requests
      await apiSubmitRequest(projectId, requests);

      // clear all form fields and agency selections
      store.resetAllFormFields();

      Notify.info({
        title: 'Submit Data Requests',
        message: 'Your data requests have been submitted successfully.',
      });

      capturePosthogEvent(POSTHOG_EVENTS.civilsearchRequestSubmitted, {
        project_id: projectId,
        agency_ids: requests.map((r) => r.agencyId),
        request_count: requests.length,
      });

      setIsProcessing(false);
      store.fetchData(projectId);
      navigate({ to: '/projects/$projectId/$tabId', params: { projectId, tabId } });
    } catch {
      Notify.error({
        title: 'Submit Data Requests',
        message: 'An error occurred submitting your requests, please contact support',
      });

      setIsProcessing(false);
    }
  };

  return (
    <DrawerContainer>
      <DrawerContent className="space-y-4">
        <div className="space-y-2">
          <Label label="Assets Requested" step={1} />
          <TableV2 id="utilities-assets-requested-v1" table={table} />
        </div>

        {files}

        <ConfirmationBlock
          label="Agency Fee"
          step={2}
          visible={constraints.hasFeeConstraints}
          confirmed={formState.feesConfirmed}
          confirmedText={'✅ Agency Fee Confirmed'}
          unconfirmedText={'❌ Agency Fee Not Confirmed'}
        />

        <ConfirmationBlock
          label="Project Narrative"
          step={3}
          visible={true}
          confirmed={formState.narrativeConfirmed}
          confirmedText={'✅ Project Narrative Confirmed'}
          unconfirmedText={'❌ Project Narrative Not Confirmed'}
        />

        <div className="flex flex-col space-y-2 text-sm">
          <div>All project team members will receive notifications with updates:</div>
          <div className="flex flex-col">
            {project?.members.map((m) => (
              <div key={m.id}>{getNonNullUserName(m.user.name) || m.user.email}</div>
            ))}
          </div>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <PreviousButton onClick={() => store.toggleFormStep(-1)} />

        <Button
          data-testid="request-data-submit"
          onClick={submitRequest}
          disabled={isDisabled || isProcessing}
          loading={isProcessing}
        >
          Submit Request
        </Button>
      </DrawerFooter>
    </DrawerContainer>
  );
}

interface LabelProps {
  label: string;
  step: number;
  buttonCopy?: string;
}

const Label = ({ label, step, buttonCopy = 'Edit' }: LabelProps) => {
  return (
    <div className="flex w-full items-center justify-between">
      <div className="text-base font-semibold">{label}</div>
      <Button
        variant="secondary"
        onClick={() => {
          store.formState.step = step;
        }}
      >
        {buttonCopy}
      </Button>
    </div>
  );
};

interface ConfirmationBlockProps extends LabelProps {
  confirmedText: React.ReactNode;
  unconfirmedText: React.ReactNode;
  confirmed: boolean;
  visible: boolean;
}

const ConfirmationBlock = ({
  confirmedText,
  unconfirmedText,
  confirmed,
  visible,
  ...labelProps
}: ConfirmationBlockProps) => {
  if (!visible) return null;

  return (
    <div className="space-y-2 text-sm">
      <Label {...labelProps} />

      {confirmed ? <div className="">{confirmedText}</div> : <div className="text-red-500">{unconfirmedText}</div>}
    </div>
  );
};
